<template>
  <div :key="componentKey">
    <grid-layout
      v-if="workspace.layout"
      :layout.sync="workspace.layout"
      :col-num="12"
      :row-height="30"
      :is-draggable="editing"
      :is-resizable="editing"
      :is-mirrored="false"
      :responsive="true"
      :vertical-compact="true"
      :margin="[15, 15]"
      :use-css-transforms="true"
      class="m-n3"
    >
      <grid-item
        v-for="item in workspace.layout"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :is-resizable="workspace.widgets[item.i].configuration.isResizable && editing"
        :min-w="workspace.widgets[item.i].configuration.minW || 2"
        :min-h="workspace.widgets[item.i].configuration.minH || 2"
      >
        <el-card
          shadow="never"
          :body-style="{ height: '100%' }"
          style="height: 100%;"
        >
          <el-button
            v-if="editing"
            size="mini"
            type="primary"
            @click.native="onClickEdit(item.i)"
          >
            Edit
          </el-button>
          <el-button
            v-if="editing"
            size="mini"
            type="danger"
            @click.native="removeWidget(item.i)"
          >
            Remove
          </el-button>
          <Widget
            :key="item.i"
            :ref="item.i"
            style="height: 100%;"
            :widget="new widgets[workspace.widgets[item.i].name](workspaceComputed.widgets[item.i].configuration)"
          />
        </el-card>
      </grid-item>
    </grid-layout>
    <ConfigureDialog
      v-if="isShowConfigureDialog"
      :widgets="widgets"
      :is-show.sync="isShowConfigureDialog"
      @submitted="addNewWidget"
    />
    <EditWidgetDialog
      v-if="isShowEditWidgetDialog"
      :widget.sync="editingWidget"
      :is-show.sync="isShowEditWidgetDialog"
      @submitted="editWidget"
    />
  </div>
</template>

<script>
import hash from 'object-hash'
import VueGridLayout from 'vue-grid-layout'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { CLEAR_CACHE } from '@/store/mutation-types'
import Widget from '@/plugins/dashboard/components/Widget'
import widgets from '@/plugins/dashboard/widgets'
import ConfigureDialog from '@/plugins/dashboard/components/ConfigureDialog'
import EditWidgetDialog from '@/plugins/dashboard/components/EditWidgetDialog'

export default {
  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    ConfigureDialog,
    EditWidgetDialog,
    Widget
  },
  data () {
    return {
      actions: [
        {
          name: 'Customise',
          icon: ['fal', 'edit'],
          event: this.enterEditMode
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.refresh
        }
      ],
      workspace: {},
      editing: false,
      isShowConfigureDialog: false,
      editingWidget: {},
      isShowEditWidgetDialog: false,
      componentKey: 0
    }
  },
  computed: {
    ...mapGetters({
      getWorkspace: 'configuration/dashboard/workspace'
    }),
    widgets () {
      return widgets
    },
    hash () {
      return hash
    },
    workspaceComputed: {
      get: function () {
        if (this.$router.currentRoute.params.accountId) {
          const clonedWorkspace = this.$_.cloneDeep(this.workspace)
          Object.keys(clonedWorkspace.widgets).map(key => {
            clonedWorkspace.widgets[key].configuration.params.accountId = [this.$router.currentRoute.params.accountId]
            if (this.$_.has(clonedWorkspace.widgets[key].configuration, 'comparing')) {
              clonedWorkspace.widgets[key].configuration.comparing.map(c => {
                c.configuration.params.accountId = [this.$router.currentRoute.params.accountId]
              })
            }
          })
          return clonedWorkspace
        } else {
          return this.workspace
        }
      },
      set: function (newVal) {}
    }
  },
  watch: {
    editing: function (newVal) {
      if (newVal) {
        this.actions = [
          [
            {
              name: 'Add Widget',
              icon: ['fal', 'plus'],
              event: () => {
                this.isShowConfigureDialog = true
              }
            }
          ],
          [
            {
              name: 'Cancel',
              icon: ['fal', 'ban'],
              event: this.cancelEdit
            },
            {
              name: 'Save Changes',
              icon: ['fal', 'save'],
              event: this.saveEdit
            }
          ]
        ]
      } else {
        this.actions = [
          {
            name: 'Customise',
            icon: ['fal', 'edit'],
            event: this.enterEditMode
          },
          {
            name: 'Refresh',
            icon: ['fal', 'sync'],
            event: this.refresh
          }
        ]
      }
    }
  },
  created () {
    this.workspace = this.$_.cloneDeep(this.getWorkspace(this.$route.name))
  },
  methods: {
    ...mapActions({
      saveDashboard: 'configuration/dashboard/saveDashboard'
    }),
    ...mapMutations({
      clearCache: `cache/${CLEAR_CACHE}`
    }),
    refresh () {
      this.clearCache('widgets')
      this.componentKey += 1
    },
    enterEditMode () {
      let tempWorkspace = this.getWorkspace(this.$route.name)
      this.saveDashboard({ 'temporary-dashboard': tempWorkspace })
      this.editing = true
    },
    cancelEdit () {
      let tempWorkspace = this.$_.cloneDeep(this.getWorkspace('temporary-dashboard'))
      this.workspace = {}
      this.$nextTick(() => {
        this.workspace = tempWorkspace
      })
      this.editing = false
    },
    saveEdit () {
      this.saveDashboard({ [this.$route.name]: this.$_.cloneDeep(this.workspace) })
      this.editing = false
      this.$user.instance.saveConfiguration()
    },
    addNewWidget (widget) {
      let hashedI = hash(new Date())
      this.workspace.layout.push(
        { 'x': 0, 'y': 0, 'w': widget.configuration.minW || 3, 'h': widget.configuration.minH || 6, 'i': hashedI }
      )
      this.workspace.widgets[hashedI] = widget
    },
    removeWidget (identifier) {
      this.workspace.layout.splice(this.workspace.layout.findIndex(layout => layout.i === identifier), 1)
      delete this.workspace.widgets[identifier]
    },
    onClickEdit (identifier) {
      this.editingWidget = {
        [identifier]: this.$_.cloneDeep(this.workspace.widgets[identifier])
      }
      this.isShowEditWidgetDialog = true
    },
    editWidget (editedWidget) {
      const identifier = Object.keys(editedWidget)[0]
      this.workspace.widgets[identifier] = editedWidget[identifier]
      this.$nextTick(() => {
        this.$refs[identifier][0].loadDataAndProps()
      })
    }
  }
}

</script>

<style lang="scss">
.vue-grid-item.vue-grid-placeholder {
  background-color: #2e7bcc;
}
</style>
